/**
 * Die **Übergabe**: der kopierfertige Text, mit dem ein Mensch die Behebung
 * anderswo anstößt (Glossar, „Übergabe") — holen, in die Zwischenablage legen,
 * zur Vorschau zeigen.
 *
 * Sie steht hier und nicht in einer Ansicht, weil es sie in **zwei Zuschnitten**
 * gibt und der Dialog für beide derselbe ist (#100): je Stelle aus dem
 * Befundvorrat, je Revision aus der Revisionsansicht. Ein zweiter Dialog daneben
 * wäre eine zweite Fassung derselben Sache — mit eigenem Wortlaut, eigenem
 * Verhalten der Zwischenablage und eigenem Fehlerfall.
 *
 * Drei Festlegungen tragen sie:
 *
 * - **Der Text kommt aus dem Server.** Befundtexte und Diff-Ausschnitt stehen
 *   nicht in der Zeile, die man angeklickt hat, und ein zur Hälfte hier
 *   zusammengesetzter Text wäre ein zweiter Wortlaut derselben Sache.
 * - **Die Vorschau ist keine Zugabe.** Ein Knopf, der still etwas kopiert, sagt
 *   nicht, *was* er kopiert hat — und der Browser darf das Schreiben ablehnen.
 *   Dann ist die Vorschau der Weg zum Text.
 * - **Eine späte Antwort auf eine überholte Frage wird verworfen.** Wer zweimal
 *   hintereinander übergibt, soll die zweite Antwort sehen und nicht die, die
 *   zufällig zuletzt eintraf.
 */

import { useRef, useState } from "react";
import type { ReactElement } from "react";

import { Button, Dialog, Modal } from "../design-system";
import type { DeliveryApi, DeliveryCut } from "../api/client";
import { useDelivery } from "./delivery";
import { Notice, describe } from "./loading";
import type { Loaded } from "./loading";

/** Was die Übergabe ist — im Titel des Vorschaufensters und auf jedem Knopf. */
export const HANDOFF_TITLE = "Übergabe";

/**
 * Was der Text ist, je Zuschnitt.
 *
 * Der zweite Satz ist in beiden derselbe und sagt seit #102 die **halbe** alte
 * Aussage: Text darf hinaus, Zustand nicht (ADR-0027). „Legt kein Ticket an"
 * gilt weiter und ist jetzt die wichtigere Hälfte — neben einem Knopf „Ins
 * Ticket kommentieren" muss dastehen, was dieser Knopf ausdrücklich *nicht* tut.
 *
 * Genannt sind **beide** Wege (#104): Ein Satz, der nur einen von zwei Knöpfen
 * erklärt, lässt den anderen unerklärt dastehen.
 */
export const SITE_HANDOFF_HINT =
  "Der Text zu dieser Stelle, zum Einfügen dort, wo die Behebung geschieht — oder als " +
  "Kommentar an ein bestehendes Ticket oder als Direktnachricht an einen Menschen. Diese " +
  "Anwendung legt kein Ticket an, schließt keines und schreibt in kein Repository.";

export const REVISION_HANDOFF_HINT =
  "Der Text zu allen Stellen dieser Revision, zum Einfügen dort, wo die Behebung geschieht " +
  "— oder als Kommentar an ein bestehendes Ticket oder als Direktnachricht an einen " +
  "Menschen. Diese Anwendung legt kein Ticket an, schließt keines und schreibt in kein " +
  "Repository.";

const COPIED = "In die Zwischenablage gelegt.";

/**
 * Der Browser darf das Schreiben ablehnen — ohne sicheren Kontext oder ohne
 * Erlaubnis. Dann steht der Text hier, und die Ansicht behauptet nichts anderes.
 */
const NOT_COPIED = "Die Zwischenablage hat abgelehnt — der Text steht hier zum Kopieren.";

/** Die Übergabe, die gerade offen ist: was sie erklärt, wie weit, und ob kopiert. */
export interface Handover {
  /** Der Satz unter dem Titel — er sagt, welcher Zuschnitt gerade offen ist. */
  hint: string;
  state: Loaded<string>;
  copied: boolean;
  /**
   * Der Zuschnitt, den dieser Dialog **zustellen** darf (#102) — `null` heißt:
   * Hier gibt es keinen Weg nach außen.
   *
   * Das ist kein Sonderfall, sondern der Fall einer Stelle aus dem Bestandslauf:
   * Ohne Revision gibt es keinen Commit-Kommentar und damit keinen Ticketverweis.
   */
  cut: DeliveryCut | null;
}

/**
 * Der Zustand der Übergabe und die drei Aufrufe daran.
 *
 * `begin` bekommt den Weg zum Text als Aufruf und nicht den Gegenstand, über den
 * er entsteht: Die Stelle wird über ihren Anker gefragt, die Revision über ihre
 * Kennung, und welcher von beidem gerade gemeint ist, geht diesen Zustand nichts
 * an.
 */
export function useHandover(): {
  shown: Handover | null;
  begin: (hint: string, fetch: () => Promise<string>, cut?: DeliveryCut | null) => void;
  copyAgain: (text: string) => void;
  close: () => void;
} {
  const [shown, setShown] = useState<Handover | null>(null);
  // Eine Marke je Anfrage, und nur die zuletzt vergebene zählt. Sie ersetzt den
  // Vergleich auf den Gegenstand: Zwei Übergaben zur selben Revision sind zwei
  // Anfragen, und die zweite hebt die erste auf.
  const asked = useRef<object | null>(null);

  const begin = (
    hint: string,
    fetch: () => Promise<string>,
    cut: DeliveryCut | null = null,
  ): void => {
    const ticket = {};

    asked.current = ticket;
    setShown({ hint, state: { kind: "loading" }, copied: false, cut });

    fetch().then(
      (text) => {
        if (asked.current !== ticket) {
          return;
        }

        void copyInto(text).then((copied) => {
          if (asked.current === ticket) {
            setShown({ hint, state: { kind: "loaded", value: text }, copied, cut });
          }
        });
      },
      (error: unknown) => {
        if (asked.current === ticket) {
          setShown({
            hint,
            state: { kind: "failed", message: describe(error) },
            copied: false,
            cut,
          });
        }
      },
    );
  };

  return {
    shown,
    begin,
    copyAgain: (text: string) => {
      void copyInto(text).then((copied) =>
        setShown((previous) => (previous === null ? null : { ...previous, copied })),
      );
    },
    close: () => {
      asked.current = null;
      setShown(null);
    },
  };
}

/**
 * Der Knopf, der eine Übergabe beginnt — gleich aussehend, wo immer er steht.
 *
 * Er hält den Klick auf: In der Liste führt die Zeile an die Stelle, in der
 * Revisionsansicht steht er in der Aktionsleiste. Beides zugleich zu tun wäre für
 * niemanden das Gewollte.
 */
export function HandoffButton({ onHandOver }: { onHandOver: () => void }): ReactElement {
  return (
    <Button
      variant="outline"
      size="sm"
      icon="clipboard-copy"
      onClick={(event) => {
        event.stopPropagation();
        onHandOver();
      }}
    >
      {HANDOFF_TITLE}
    </Button>
  );
}

/**
 * Die Vorschau: derselbe Text, der in der Zwischenablage liegt — und der Weg
 * hinaus daneben.
 *
 * **Ein Dialog, mehrere Wege** (#102, #104): Die Knöpfe „Ins Ticket kommentieren"
 * und „Als Mattermost-Nachricht senden" stehen neben „Kopieren" und nicht in einer
 * dritten Spalte der ohnehin breiten Tabelle. Ein Knopf bedient genau einen
 * Weg. Der Kopierweg bleibt: Er ist der Rückfall, der immer funktioniert.
 *
 * **Die Vorschau ist die Bestätigung.** Es gibt keinen zweiten Rückfragedialog —
 * wer den Text gelesen hat und den Knopf drückt, hat entschieden (ADR-0027).
 *
 * `client` ist die Naht zur Zustellung; ohne ihn zeigt der Dialog nur den Text.
 */
export function HandoffDialog({
  shown,
  client = null,
  onCopy,
  onClose,
}: {
  shown: Handover;
  client?: DeliveryApi | null;
  onCopy: (text: string) => void;
  onClose: () => void;
}): ReactElement {
  const text = shown.state.kind === "loaded" ? shown.state.value : null;
  // Erst wenn der Rumpf dasteht: Zugestellt wird, was in der Vorschau steht, und
  // ein Knopf über einem leeren Feld hätte keinen Gegenstand.
  const delivery = useDelivery(text === null ? null : client, shown.cut);

  return (
    <Modal onClose={onClose}>
      <Dialog
        title={HANDOFF_TITLE}
        description={shown.hint}
        width={760}
        onClose={onClose}
        footer={
          // **Eine** umbrechende Zeile und nicht vier lose Knöpfe (#116): Die
          // Aktionsleiste des Dialogs setzt ihre Kinder ohne Umbruch nebeneinander,
          // und mit vollen Zielnamen — „Als Mattermost-Nachricht an … senden",
          // „Ins Ticket owner/repo#n kommentieren" — sind die vier Beschriftungen
          // breiter als der Dialog. Gestaucht träten sie links und rechts aus ihm
          // heraus. Die Beschriftungen bleiben vollständig, denn der volle
          // Zielname ist die Zusage aus #112; also bricht die Zeile um.
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: "var(--sp-4)",
              width: "100%",
            }}
          >
            <Button variant="outline" onClick={onClose}>
              Schließen
            </Button>
            {text === null ? null : (
              <Button
                // Der Kopierweg gibt die Betonung ab, sobald es einen Weg hinaus
                // gibt — er bleibt trotzdem stehen: Er ist der Rückfall, der immer
                // funktioniert (ADR-0027).
                variant={
                  delivery.ticketButton === null && delivery.chatButton === null
                    ? "primary"
                    : "outline"
                }
                icon="clipboard-copy"
                onClick={() => onCopy(text)}
              >
                Kopieren
              </Button>
            )}
            {delivery.chatButton}
            {delivery.ticketButton}
          </div>
        }
      >
        {shown.state.kind === "loading" ? (
          <Notice text="Wird zusammengestellt ..." glyph="timer" />
        ) : null}
        {shown.state.kind === "failed" ? (
          <Notice text={shown.state.message} glyph="unplug" color="var(--ac-red)" />
        ) : null}

        {text === null ? null : delivery.panel}

        {text === null ? null : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}>
            <span
              style={{
                fontSize: "var(--fs-11)",
                color: shown.copied ? "var(--fg-6)" : "var(--ac-amber)",
                textWrap: "pretty",
              }}
            >
              {shown.copied ? COPIED : NOT_COPIED}
            </span>
            <pre
              className="erp-scroll"
              style={{
                margin: 0,
                maxHeight: 420,
                overflow: "auto",
                padding: "var(--sp-5) var(--sp-6)",
                background: "var(--bg-sunken)",
                border: "1px solid var(--line-f)",
                borderRadius: "var(--r-field)",
                color: "var(--text-body)",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--fs-11)",
                lineHeight: "var(--lh-normal)",
                whiteSpace: "pre-wrap",
              }}
            >
              {text}
            </pre>
          </div>
        )}
      </Dialog>
    </Modal>
  );
}

/**
 * Den Text in die Zwischenablage legen — und sagen, ob es geklappt hat.
 *
 * Der Browser darf ablehnen: ohne sicheren Kontext, ohne Erlaubnis, oder weil es
 * die Schnittstelle gar nicht gibt. Ein stiller Fehlschlag wäre der schlechteste
 * Ausgang — die Vorschau behauptete dann, der Text liege bereit.
 */
async function copyInto(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);

    return true;
  } catch {
    return false;
  }
}
